import { getCLS, getFID, getLCP, getFCP, getTTFB, onINP } from "web-vitals";

/**
 * Конфигурация пороговых значений для Web Vitals
 * Основано на рекомендациях Google
 */
const THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 }, // Largest Contentful Paint (ms)
  FID: { good: 100, needsImprovement: 300 }, // First Input Delay (ms)
  CLS: { good: 0.1, needsImprovement: 0.25 }, // Cumulative Layout Shift
  FCP: { good: 1800, needsImprovement: 3000 }, // First Contentful Paint (ms)
  TTFB: { good: 800, needsImprovement: 1800 }, // Time to First Byte (ms)
  INP: { good: 200, needsImprovement: 500 }, // Interaction to Next Paint (ms)
};

/**
 * Определяет рейтинг метрики
 * @param {string} name - Название метрики
 * @param {number} value - Значение метрики
 * @returns {"good" | "needs-improvement" | "poor"}
 */
const getRating = (name, value) => {
  const threshold = THRESHOLDS[name];
  if (!threshold) return "unknown";

  if (value <= threshold.good) return "good";
  if (value <= threshold.needsImprovement) return "needs-improvement";
  return "poor";
};

/**
 * Форматирует метрику для логирования
 * @param {object} metric - Объект метрики от web-vitals
 * @returns {object}
 */
const formatMetric = (metric) => ({
  name: metric.name,
  value: metric.value,
  rating: getRating(metric.name, metric.value),
  delta: metric.delta,
  id: metric.id,
  navigationType: metric.navigationType,
  entries: metric.entries?.length || 0,
});

/**
 * Логирует метрику в консоль с цветовой индикацией
 * @param {object} metric - Форматированная метрика
 */
const logMetric = (metric) => {
  const colors = {
    good: "color: #0cce6b",
    "needs-improvement": "color: #ffa400",
    poor: "color: #ff4e42",
    unknown: "color: #gray",
  };

  const style = colors[metric.rating] || colors.unknown;

  console.log(
    `%c[Web Vitals] ${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`,
    style,
  );
};

/**
 * Отправляет метрику на сервер аналитики
 * @param {object} metric - Форматированная метрика
 */
const sendToAnalytics = (metric) => {
  // Отправка на сервер отключена - эндпоинт не реализован
  // TODO: Реализовать /api/analytics/vitals на бэкенде если нужна аналитика
  return;
};

/**
 * Обработчик метрики
 * @param {object} metric - Объект метрики от web-vitals
 * @param {object} options - Опции
 */
const handleMetric = (metric, options = {}) => {
  const formatted = formatMetric(metric);

  // Логирование в development
  if (process.env.NODE_ENV === "development" || options.debug) {
    logMetric(formatted);
  }

  // Отправка на сервер в production
  if (process.env.NODE_ENV === "production" && options.sendToServer !== false) {
    sendToAnalytics(formatted);
  }

  // Вызов пользовательского обработчика
  if (typeof options.onMetric === "function") {
    options.onMetric(formatted);
  }
};

/**
 * Инициализирует мониторинг Web Vitals
 * @param {object} options - Опции конфигурации
 * @param {boolean} options.debug - Включить логирование в development
 * @param {boolean} options.sendToServer - Отправлять на сервер
 * @param {function} options.onMetric - Callback для каждой метрики
 */
export const reportWebVitals = (options = {}) => {
  const handler = (metric) => handleMetric(metric, options);

  // Core Web Vitals
  getCLS(handler);
  getFID(handler);
  getLCP(handler);

  // Additional metrics
  getFCP(handler);
  getTTFB(handler);

  // INP (Interaction to Next Paint) - web-vitals 3.x
  onINP(handler);
};

/**
 * Возвращает объект с текущими пороговыми значениями
 * @returns {object}
 */
export const getThresholds = () => ({ ...THRESHOLDS });

/**
 * Хук для получения метрик в реальном времени (для отладочной панели)
 */
let metricsStore = {};

export const collectMetrics = () => {
  reportWebVitals({
    debug: false,
    sendToServer: false,
    onMetric: (metric) => {
      metricsStore[metric.name] = metric;
    },
  });
};

export const getCollectedMetrics = () => ({ ...metricsStore });

export default reportWebVitals;
